import type { ModuleLoaderResolveId } from '../ModuleLoader';
import type { CustomPluginOptions, Plugin, PluginContext, ResolveIdResult } from '../rollup/types';
import type { PluginDriver, ReplaceContext } from './PluginDriver';
import { BLANK, EMPTY_OBJECT } from './blank';

export function resolveIdViaPlugins(
	source: string,
	importer: string | undefined,
	pluginDriver: PluginDriver,
	moduleLoaderResolveId: ModuleLoaderResolveId,
	skip: readonly { importer: string | undefined; plugin: Plugin; source: string }[] | null,
	customOptions: CustomPluginOptions | undefined,
	isEntry: boolean,
	attributes: Record<string, string>,
	importerAttributes: Record<string, string> | undefined
): Promise<[NonNullable<ResolveIdResult>, Plugin] | null> {
	let skipped: Set<Plugin> | null = null;
	let replaceContext: ReplaceContext | null = null;
	if (skip) {
		skipped = new Set();
		for (const skippedCall of skip) {
			if (source === skippedCall.source && importer === skippedCall.importer) {
				skipped.add(skippedCall.plugin);
			}
		}
		replaceContext = (pluginContext, plugin): PluginContext => ({
			...pluginContext,
			resolve: (
				source,
				importer,
				{ attributes, custom, isEntry, skipSelf, importerAttributes } = BLANK
			) => {
				skipSelf ??= true;
				if (
					skipSelf &&
					skip.findIndex(skippedCall => {
						return (
							skippedCall.plugin === plugin &&
							skippedCall.source === source &&
							skippedCall.importer === importer
						);
					}) !== -1
				) {
					// This means that the plugin recursively called itself
					// Thus returning Promise.resolve(null) in purpose of fallback to default behavior of `resolveId` plugin hook.
					return Promise.resolve(null);
				}
				return moduleLoaderResolveId(
					source,
					importer,
					custom,
					isEntry,
					attributes || EMPTY_OBJECT,
					importerAttributes,
					skipSelf ? [...skip, { importer, plugin, source }] : skip
				);
			}
		});
	}
	return pluginDriver.hookFirstAndGetPlugin(
		'resolveId',
		[source, importer, { attributes, custom: customOptions, importerAttributes, isEntry }],
		replaceContext,
		skipped
	);
}
